import {
    Button,
    Col,
    Form,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import {
    IGroupedOption,
    IModal,
    IOption,
} from "Pages/Tools/EmailRules/EmailRules";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import makeAnimated from "react-select/animated";

import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import styles from "Pages/Tools/EmailRules/Components/TestModal/TestModal.module.css";
import { useState } from "react";

interface IProps {
    readonly integrationOptions: IGroupedOption[];
    readonly modal: IModal;
    readonly onSetModal: (modal: IModal) => void;
}

interface IFormData {
    integration: IOption;
    threshhold: IOption;
    email_on_any_actions: boolean;
}

interface IErrors {
    integration: string;
    threshhold: string;
    email_on_any_actions: string;
}

interface IResults {
    users: string[];
    cc_users: string[];
}

export default function DeleteModal({
    integrationOptions,
    modal,
    onSetModal,
}: IProps) {
    const defaultFormData = {
        integration: { value: "", label: "" },
        threshhold: { value: "Complete", label: "Complete" },
        email_on_any_actions: false,
    };

    const [formData, setFormData] = useState<IFormData>(defaultFormData);
    const [isTesting, setIsTesting] = useState(false);
    const [results, setResults] = useState<IResults>();
    const [errors, setErrors] = useState<IErrors>({
        integration: "",
        threshhold: "",
        email_on_any_actions: "",
    });

    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    /** Hook for react-select inputs to make them animated when deleting */
    const animated = makeAnimated();

    const toggle = () => {
        onSetModal("");
        setFormData(defaultFormData);
        setResults(undefined);
    };

    /** With react-select input, instead of e (change event), we get the value(s) */
    const handleSelectChange = (newValue: any, key: string) => {
        setFormData({ ...formData, [key]: newValue });
    };

    /** Form validation */
    const validate = () => {
        let valid = true;
        const newErrors = { ...errors };

        if (formData.integration.value === "") {
            newErrors.integration = "Integration is required";
            valid = false;
        } else {
            newErrors.integration = "";
        }
        setErrors(newErrors);

        return valid;
    };

    /** Fires on button click. Makes a request to the test endpoint */
    const test = async () => {
        if (!validate()) return;
        setIsTesting(true);

        const integrationValue = encodeURIComponent(formData.integration.value);
        const thresholdValue = encodeURIComponent(formData.threshhold.value);

        const url = `/api/email_rules/test/${integrationValue}/${thresholdValue}`;
        const response = await get(url);

        let data: Response | IResults;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Test email rules response: ", data);

        if (response.ok) {
            setResults(data as IResults);
        } else {
            newAlert({
                message: "Error testing email rules.",
            });
        }

        setIsTesting(false);
    };

    /** Calculates styles to pass to react-select input, because it is a pain
     * and does not work with Reactstrap's FormFeedback
     */
    const selectStyles = (id: keyof IErrors) => {
        return {
            control: (provided: any, state: any) => ({
                ...provided,
                borderColor: state.isFocused
                    ? errors[id] !== ""
                        ? "#dc3545"
                        : "#80bdff"
                    : errors[id] !== ""
                    ? "#dc3545"
                    : "#ced4da",
                boxShadow: state.isFocused
                    ? errors[id] !== ""
                        ? "0 0 0 0.2rem rgba(220, 53, 69, .25)"
                        : "0 0 0 0.2rem rgba(0,123,255,.25)"
                    : "none",
                "&:hover": {
                    borderColor: state.isFocused
                        ? errors[id] !== ""
                            ? "#dc3545"
                            : "#80bdff"
                        : errors[id] !== ""
                        ? "#dc3545"
                        : "#ced4da",
                },
            }),
        };
    };

    /** Options for threshhold input. Yes, threshhold is mispelled in the MySql database. */
    const threshholdOptions = ["Complete", "Warning", "Errored"].map(
        (option) => ({
            value: option,
            label: option,
        })
    );

    return (
        <Modal isOpen={modal === "test"} toggle={toggle}>
            <ModalHeader>Test Rule(s)</ModalHeader>
            <ModalBody>
                <i>
                    This will send actual test emails based on the selected options and
                    display the recipients here.
                </i>
                <div style={{ marginBottom: "1rem" }}></div>
                <Form>
                    {/* Integrations */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label
                                    for="integrations"
                                    className={styles.label}
                                >
                                    Select Integrations:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <CreatableSelect
                                id="integrations"
                                closeMenuOnSelect={true}
                                components={animated}
                                options={integrationOptions}
                                value={formData.integration}
                                onChange={(e) =>
                                    handleSelectChange(e, "integration")
                                }
                                styles={selectStyles("integration")} // react-select doesn't work with bootstrap's form validation
                            />
                            {errors.integration !== "" && (
                                <div className="invalid-feedback d-block">
                                    {errors.integration}
                                </div>
                            )}
                        </Col>
                    </FormGroup>

                    {/* Threshold/Status/Trigger */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label
                                    for="threshhold"
                                    className={styles.label}
                                >
                                    Results:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Select
                                classNamePrefix="rs"
                                id="threshhold"
                                closeMenuOnSelect={true}
                                components={animated}
                                options={threshholdOptions}
                                value={formData.threshhold}
                                onChange={(e) =>
                                    handleSelectChange(e, "threshhold")
                                }
                                styles={selectStyles("threshhold")} // react-select doesn't work with bootstrap's form validation
                            />
                        </Col>
                    </FormGroup>
                </Form>
                {results && (
                    <>
                        <hr />
                        <Row>
                            <Col sm={3}>
                                <b>Emails sent to users:</b>
                            </Col>
                            <Col sm={9}>
                                <ul>
                                    {results.users
                                        .filter((u) => u !== "")
                                        .map((u) => (
                                            <li key={u}>{u}</li>
                                        ))}
                                </ul>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={3}>
                                <b>And CC'd users:</b>
                            </Col>
                            <Col sm={9}>
                                <ul>
                                    {results.cc_users
                                        .filter((u) => u !== "")
                                        .map((u) => (
                                            <li key={u}>{u}</li>
                                        ))}
                                </ul>
                            </Col>
                        </Row>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={test}>
                    {isTesting ? "Testing..." : "Test"}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
